import { Component } from '@theme/component';

/**
 * The country / language selector (sections/country-selector.liquid).
 *
 * The section is fully functional before this file loads: languages are anchors to their locale
 * root, the country picker is reachable through a `:target` fragment link, and licensee countries
 * are plain external links. This component only upgrades that baseline.
 *
 * Two upgrades:
 *
 * - Step switching without touching `location.hash`, so opening the picker never adds a history
 *   entry and never fights the `:target` CSS fallback.
 * - Country and language entries submit the localization form instead of navigating. That matters
 *   for countries: their href is the market's URL (the coarsest thing Liquid can express, since
 *   there is no GET route for switching country), while the form carries the exact `country_code`
 *   so currency and shipping resolve correctly.
 *
 * Clicks are handled by one delegated listener rather than `on:click` attributes on each entry —
 * the store has 237 countries, and the framework's declarative binding reports `event.target` as
 * the element carrying the attribute, which delegation needs to see through.
 *
 * A third, purely visual upgrade: each region's list is laid out column-major, so the countries
 * read alphabetically DOWN each column rather than across the rows. See `#reflow`.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The hidden localization form.
 * @property {HTMLInputElement} countryInput - Hidden `country_code` input.
 * @property {HTMLInputElement} languageInput - Hidden `language_code` input.
 *
 * @extends {Component<Refs>}
 */
class CountrySelectorComponent extends Component {
  requiredRefs = ['form', 'countryInput', 'languageInput'];

  /** @type {AbortController | undefined} */
  #controller;

  /**
   * Watches every region list for a change of inline size — a viewport resize, a region being
   * expanded, the step becoming visible, the overlay opening — because the column count each of
   * those resolves to is what the row count has to be derived from.
   *
   * @type {ResizeObserver | undefined}
   */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal: this.#controller.signal });
    this.#revealMedia();
    this.#observeLists();
  }

  /**
   * Flag the brand image once it has actually decoded so CSS can fade it in. Guarded on `complete`
   * because the image is usually already decoded — the overlay warms this section shortly after
   * page load — and a `load` listener added after the fact would never fire.
   */
  #revealMedia() {
    const image = this.querySelector('.country-selector__media-image');
    if (!(image instanceof HTMLImageElement)) return;

    const reveal = () => image.setAttribute('data-loaded', '');

    if (image.complete && image.naturalWidth > 0) reveal();
    else image.addEventListener('load', reveal, { once: true, signal: this.#controller?.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = undefined;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  /**
   * Lay the country lists out column-major.
   *
   * The lists are already sorted by name, but a grid's default row-major flow means that order
   * reads across the rows — column one holding Albania, Bulgaria, Cyprus rather than Albania,
   * Andorra, Austria. Alphabetical order is only usable if it runs down a column, the way a
   * newspaper or an index sets it.
   *
   * `grid-auto-flow: column` gives that, but only with an explicit row count: the column count
   * comes from `repeat(auto-fill, ...)`, so without a row track list the browser fills one column
   * to an arbitrary height and spills the rest sideways. The row count is
   * `ceil(countries / columns)`, which needs the resolved column count — a used value, not
   * something Liquid or a media query can know, since it depends on how many 12rem tracks fit the
   * panel at this viewport. Hence measuring here.
   *
   * Without JavaScript the lists stay row-major: still complete, still sorted, just read across
   * instead of down.
   */
  #observeLists() {
    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) this.#reflow(entry.target);
    });

    for (const list of this.querySelectorAll('.country-selector__countries')) {
      this.#resizeObserver.observe(list);
    }
  }

  /**
   * @param {Element} list - One region's `<ul>` of countries.
   */
  #reflow(list) {
    if (!(list instanceof HTMLElement)) return;

    // Zero width means nothing to measure: a collapsed region, or the picker step before it is
    // shown. The observer calls back again once it has a box.
    if (!list.clientWidth) return;

    // Measured with the previous pass undone, which is not optional. Column flow plus a row count
    // pushes any surplus item into an *implicit* column, and the resolved
    // `grid-template-columns` counts those too — so reading it while they exist confirms whichever
    // count the last pass produced rather than the one the stylesheet asks for at this width.
    // Crossing a breakpoint is exactly when the two differ: 47 Asian countries in 12 rows read
    // back as four columns on a 390px phone whose stylesheet says two, and the count then never
    // corrected itself.
    delete list.dataset.columnFlow;
    list.style.gridTemplateRows = '';

    // Reading a resolved track list forces the layout that has just been invalidated, so this is
    // the stylesheet's own answer: `repeat(auto-fill, ...)` already resolved into one length per
    // track, with no implicit columns left to confuse it.
    const tracks = getComputedStyle(list).gridTemplateColumns;
    const columns = tracks && tracks !== 'none' ? tracks.split(' ').length : 1;

    list.style.gridTemplateRows = `repeat(${Math.ceil(list.childElementCount / columns)}, auto)`;
    list.dataset.columnFlow = '';
  }

  /** Show the region/country picker. Bound declaratively from Liquid. */
  showRegions(event) {
    event?.preventDefault();
    this.dataset.activeStep = 'regions';
    this.#focusStep('.country-selector__step--regions');
  }

  /** Return to the overview. Bound declaratively from Liquid. */
  showOverview(event) {
    event?.preventDefault();
    this.dataset.activeStep = 'overview';
    this.#focusStep('.country-selector__step--overview');
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    // Let the browser handle anything that isn't a plain left click, so modifier-clicking a country
    // still opens it in a new tab the way the anchor promises.
    if (event.defaultPrevented || event.button !== 0) return;

    // A choice is already on its way to the server and the page is about to be replaced. A second
    // click would either re-submit the form or toggle a region on a page with seconds to live, so
    // the picker goes inert until the navigation lands.
    if (this.hasAttribute('aria-busy')) {
      event.preventDefault();
      return;
    }

    const target = /** @type {HTMLElement} */ (event.target);

    // On the desktop rail, clicking the region that is already open would close it — `name`
    // grouping only enforces "at most one open", not "exactly one" — leaving the grey surface
    // with no list on it. There a click only ever moves the selection to another region.
    // (Enter/Space on a focused summary arrives here as a click too, so keyboard is covered.)
    // The mobile accordion keeps the native toggle: collapsing the open region back to the five
    // rows is normal accordion behaviour, and nothing looks broken with every region closed.
    // 768px is the stylesheet's breakpoint between the two layouts.
    const summary = target.closest?.('.country-selector__region-summary');
    if (
      summary?.closest('details')?.open &&
      window.matchMedia('(min-width: 768px)').matches
    ) {
      event.preventDefault();
      return;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const entry = target.closest?.('[data-country-code], [data-language-code]');

    if (!(entry instanceof HTMLAnchorElement)) return;
    // Licensee countries are genuine external links — never intercept them, and never rewrite the
    // basic_country_splash=remain handshake the destination site depends on.
    if (entry.hasAttribute('data-country-external')) return;

    const countryCode = entry.dataset.countryCode;
    const languageCode = entry.dataset.languageCode;

    if (!countryCode && !languageCode) return;

    event.preventDefault();

    if (countryCode) this.refs.countryInput.value = countryCode;
    if (languageCode) this.refs.languageInput.value = languageCode;

    // The submit is a full POST → redirect → render round trip — seconds, on a slow connection —
    // and until it lands the old page just sits there. Without this the click looks like it did
    // nothing, and the shopper reads the still-on-screen page (its announcement bar included) as
    // "the switch didn't work". See the aria-busy rules in the section's stylesheet.
    this.setAttribute('aria-busy', 'true');
    entry.setAttribute('aria-busy', 'true');

    this.#clearLocalizationCookies();
    this.refs.form.submit();
  };

  /**
   * Expire every localization cookie this surface can be reading before the form re-sets it.
   *
   * Shopify scopes the `localization` cookie to the locale root of whichever URL wrote it: a
   * plain GET carrying `?country=US` on a market presence stamps `localization=US; path=/en-row`,
   * while the localization POST writes the new choice at `path=/`. Both survive side by side, and
   * on every URL under the deeper path the browser sends both with the stale one first — so the
   * server keeps answering with the OLD country there, while root-scoped pages show the new one.
   * To the shopper the switch looks like it silently failed on the very page they made it from
   * (verified on production: one `?country=` visit poisons the jar for every later switch).
   *
   * Expiring the cookie at both scopes — the site root and this form's locale root — just before
   * the submit means the POST writes onto a clean jar and every surface reads the same single
   * cookie afterwards. The cookie is host-only and not HttpOnly, so `document.cookie` can expire
   * it; the deletion must name the exact path the cookie was set with, which is why both are
   * spelled out rather than relying on one write.
   */
  #clearLocalizationCookies() {
    const action = this.refs.form.getAttribute('action') ?? '';
    const root = action.replace(/\/localization$/, '') || '/';

    for (const path of new Set(['/', root])) {
      document.cookie = `localization=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }

  /**
   * A back/forward-cache restore brings the page back exactly as it was left — busy state
   * included, with no navigation pending any more. Fires on normal loads too, where `persisted`
   * is false and there is nothing to undo.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    this.removeAttribute('aria-busy');
    for (const entry of this.querySelectorAll('[data-country-code][aria-busy], [data-language-code][aria-busy]')) {
      entry.removeAttribute('aria-busy');
    }
  };

  /**
   * Move focus to the newly shown step so keyboard and screen-reader users land in it rather than
   * staying on a control that is now hidden.
   *
   * @param {string} selector
   */
  #focusStep(selector) {
    const step = this.querySelector(selector);
    if (!(step instanceof HTMLElement)) return;

    const heading = step.querySelector('.country-selector__heading');
    const focusTarget = heading instanceof HTMLElement ? heading : step;

    focusTarget.setAttribute('tabindex', '-1');
    focusTarget.focus({ preventScroll: true });
  }
}

if (!customElements.get('country-selector-component')) {
  customElements.define('country-selector-component', CountrySelectorComponent);
}
